import { createClient } from "@supabase/supabase-js";

const url = import.meta.env.VITE_SUPABASE_URL  || "";
const key = import.meta.env.VITE_SUPABASE_ANON_KEY || "";

export const supabase = url && key ? createClient(url, key) : null;

let _userId      = null;
let _accountId   = null;
let _workspaceId = null;

if (supabase) {
  supabase.auth.onAuthStateChange((_, session) => {
    _userId = session?.user?.id || null;
    if (!session) { _accountId = null; _workspaceId = null; }
  });
}

export async function ensureAuth() {
  if (!supabase) throw new Error("Supabase not configured");
  if (_userId) return _userId;
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.user) { _userId = session.user.id; return _userId; }
  throw new Error("Not authenticated");
}

// Returns the account_id to use for all data queries.
// For owners it equals their own user_id; for staff/managers it's the owner's id.
export async function getAccountId() {
  if (_accountId) return _accountId;
  const uid = await ensureAuth();
  const { data } = await supabase.from("profiles").select("account_id").eq("id", uid).single();
  _accountId = data?.account_id || uid;
  return _accountId;
}

export function clearAccountCache() { _accountId = null; }

// ── Workspace ─────────────────────────────────────────────────────────────────

export async function getWorkspaceId() {
  if (_workspaceId) return _workspaceId;
  const uid = await ensureAuth();
  const { data: profile } = await supabase.from("profiles").select("active_workspace_id").eq("id", uid).single();
  if (profile?.active_workspace_id) {
    _workspaceId = profile.active_workspace_id;
    return _workspaceId;
  }
  // Fallback: find first workspace this user owns or belongs to and set it active
  const { data: ws } = await supabase.from("workspaces").select("id").eq("owner_id", uid).order("created_at").limit(1).single();
  if (ws?.id) {
    _workspaceId = ws.id;
    await supabase.from("profiles").update({ active_workspace_id: ws.id }).eq("id", uid);
    return _workspaceId;
  }
  const { data: member } = await supabase.from("workspace_members").select("workspace_id").eq("user_id", uid).order("created_at").limit(1).single();
  if (member?.workspace_id) {
    _workspaceId = member.workspace_id;
    await supabase.from("profiles").update({ active_workspace_id: member.workspace_id }).eq("id", uid);
    return _workspaceId;
  }
  return null;
}

export function clearWorkspaceCache() { _workspaceId = null; }

export async function getWorkspaces() {
  if (!supabase) return [];
  const uid = await ensureAuth();
  // 1. Owned workspaces via RLS (anon client).
  const { data: owned } = await supabase.from("workspaces").select("id, name, category, owner_id, plan, enabled_modules, custom_label, image_quality, white_label_enabled, product_kind, index_agent_gsc_property, index_agent_sitemap_url, index_agent_url_cap, index_agent_url_count, index_agent_last_gsc_sync_at, index_agent_digest_enabled, index_agent_last_digest_sent_at, index_agent_monthly_report_enabled, index_agent_last_monthly_report_sent_at").order("created_at");
  // 2. Member-only workspaces via service-role server endpoint (workspaces RLS
  //    can't express workspace_members membership without infinite recursion).
  let members = [];
  try {
    const r = await fetch("/api/me/member-workspaces", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ callerUserId: uid }),
    });
    if (r.ok) {
      const j = await r.json();
      members = j.workspaces || [];
    }
  } catch { /* fall through — show what we have */ }
  // 3. Merge by id (owned first, then any extra members).
  const seen = new Set((owned || []).map(w => w.id));
  const merged = [...(owned || [])];
  for (const m of members) {
    if (!seen.has(m.id)) { merged.push(m); seen.add(m.id); }
  }
  return merged;
}

// ── Custom plan requests ──────────────────────────────────────────────────────

export async function submitCustomPlanRequest(fields) {
  if (!supabase) throw new Error("Supabase not configured");
  const wid = await getWorkspaceId().catch(() => null);
  const { error } = await supabase.from("custom_plan_requests").insert({ ...fields, workspace_id: wid });
  if (error) throw new Error(error.message);
}

// ── Admin functions (requires service role or super_admin) ────────────────────
// RLS RULE: reads below use the anon client (RLS allows super_admin SELECT).
// Any WRITE that crosses workspace ownership MUST go through a server endpoint
// (POST /api/admin/update-workspace, /api/admin/update-request, etc.) so that
// supabaseAdmin (service role) is used. The anon client silently discards
// cross-ownership writes without throwing — the save appears to work but isn't.

export async function getIsSuperAdmin() {
  if (!supabase) return false;
  try {
    const uid = await ensureAuth();
    const { data } = await supabase.from("profiles").select("is_super_admin").eq("id", uid).single();
    return data?.is_super_admin === true;
  } catch { return false; }
}

export async function getIsAgencyAdmin() {
  if (!supabase) return false;
  try {
    const uid = await ensureAuth();
    const { data } = await supabase.from("profiles").select("is_agency_admin").eq("id", uid).single();
    return data?.is_agency_admin === true;
  } catch { return false; }
}

export async function getAgencyWorkspaces() {
  if (!supabase) return [];
  try {
    const uid = await ensureAuth();
    // Get workspace IDs where user is a member
    const { data: memberships } = await supabase
      .from("workspace_members").select("workspace_id").eq("user_id", uid);
    const wids = (memberships || []).map(m => m.workspace_id);
    if (!wids.length) return [];
    // Return workspaces where user is member but NOT owner
    const { data } = await supabase
      .from("workspaces")
      .select("id, name, category, owner_id, plan, enabled_modules, custom_label")
      .in("id", wids)
      .neq("owner_id", uid)
      .order("created_at", { ascending: false });
    return data || [];
  } catch { return []; }
}

export async function adminGetRequests() {
  if (!supabase) return [];
  const { data } = await supabase.from("custom_plan_requests").select("*").order("created_at", { ascending: false });
  return data || [];
}

export async function adminProvisionPlan(workspaceId, plan, enabledModules, customLabel) {
  const r = await fetch("/api/admin/update-workspace", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ workspaceId, updates: { plan, enabled_modules: enabledModules, custom_label: customLabel || null } }),
  });
  const d = await r.json();
  if (!r.ok) throw new Error(d.error || "Update failed");
}

export async function adminUpdateRequestStatus(requestId, status, adminNotes) {
  const updates = { status, admin_notes: adminNotes };
  if (status === "provisioned") updates.provisioned_at = new Date().toISOString();
  const r = await fetch("/api/admin/update-request", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ requestId, updates }),
  });
  const d = await r.json();
  if (!r.ok) throw new Error(d.error || "Update failed");
}

export async function adminGetAllWorkspaces() {
  if (!supabase) return [];
  const { data, error } = await supabase.from("workspaces").select("id, name, category, owner_id, plan, enabled_modules, custom_label, plan_template_id, image_quality, white_label_enabled, article_prompt, created_at, lead_agent_monthly_cap, lead_agent_used_this_month, lead_agent_enrichment_cap, lead_agent_enrichment_used, lead_agent_outreach_cap, lead_agent_outreach_used, call_lookup_monthly_cap, call_lookup_used_this_month, product_kind, index_agent_gsc_property, index_agent_sitemap_url, index_agent_url_cap, index_agent_url_count, index_agent_last_gsc_sync_at, citation_index_check_monthly_cap, citation_index_check_used_this_month, citation_describe_monthly_cap, citation_describe_used_this_month, stack_build_monthly_cap, stack_build_used_this_month").order("created_at", { ascending: false });
  if (error) {
    console.error("[adminGetAllWorkspaces]", error);
    throw new Error(error.message || "Failed to list workspaces");
  }
  return data || [];
}

// ── White Label ───────────────────────────────────────────────────────────────

export async function getWhiteLabelConfig() {
  if (!supabase) return null;
  const wid = await getWorkspaceId().catch(() => null);
  if (!wid) return null;
  const { data } = await supabase.from("white_label_configs").select("*").eq("workspace_id", wid).maybeSingle();
  return data || null;
}

export async function getWhiteLabelConfigByDomain(hostname) {
  if (!supabase) return null;
  const { data } = await supabase.from("white_label_configs").select("*").eq("custom_domain", hostname).maybeSingle();
  return data || null;
}

export async function getWhiteLabelConfigBySubdomain(subdomain) {
  if (!supabase) return null;
  const { data } = await supabase.from("white_label_configs").select("*").eq("subdomain", subdomain).maybeSingle();
  return data || null;
}

export async function saveWhiteLabelConfig(config) {
  if (!supabase) throw new Error("Supabase not configured");
  const wid = await getWorkspaceId();
  const { error } = await supabase.from("white_label_configs").upsert(
    { ...config, workspace_id: wid, updated_at: new Date().toISOString() },
    { onConflict: "workspace_id" }
  );
  if (error) throw new Error(error.message);
}

// ── Plan Templates ────────────────────────────────────────────────────────────

export async function adminGetTemplates() {
  if (!supabase) return [];
  const { data } = await supabase.from("plan_templates").select("*").order("sort_order").order("created_at");
  return data || [];
}

export async function adminSaveTemplate(template) {
  if (!supabase) throw new Error("Supabase not configured");
  const payload = { ...template, updated_at: new Date().toISOString() };
  if (template.id) {
    const { id, created_at, ...rest } = payload;
    const { error } = await supabase.from("plan_templates").update(rest).eq("id", id);
    if (error) throw new Error(error.message);
  } else {
    const { id, created_at, updated_at, ...rest } = payload;
    const { error } = await supabase.from("plan_templates").insert(rest);
    if (error) throw new Error(error.message);
  }
}

export async function adminDeleteTemplate(id) {
  if (!supabase) throw new Error("Supabase not configured");
  const { error } = await supabase.from("plan_templates").delete().eq("id", id);
  if (error) throw new Error(error.message);
}

export async function adminAssignTemplate(workspaceId, template) {
  const r = await fetch("/api/admin/update-workspace", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ workspaceId, updates: {
      plan_template_id:    template.id,
      plan:                "custom",
      enabled_modules:     template.modules,
      custom_label:        template.name,
      white_label_enabled: !!template.white_label_enabled,
      image_quality:       template.image_quality || "standard",
      product_kind:        template.product_kind || "full",
    }}),
  });
  const d = await r.json();
  if (!r.ok) throw new Error(d.error || "Update failed");
}

// Resolve the caller's effective role for one workspace. Falls back to
// "owner" on any failure so we don't accidentally hide modules from a
// legit owner due to a network blip — wrong-direction failure is showing
// too much, not too little, which the workspace owner will tolerate.
export async function getMyWorkspaceRole(workspaceId) {
  if (!workspaceId) return "owner";
  try {
    const callerUserId = await ensureAuth();
    const r = await fetch("/api/me/workspace-role", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ callerUserId, workspaceId }),
    });
    if (!r.ok) return "owner";
    const j = await r.json();
    return j.role || "owner";
  } catch { return "owner"; }
}

export async function adminGetUserEmails(userIds) {
  if (!Array.isArray(userIds) || userIds.length === 0) return {};
  const callerUserId = await ensureAuth();
  const r = await fetch("/api/admin/user-emails", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ callerUserId, userIds }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `Failed to resolve user emails (HTTP ${r.status})`);
  }
  const j = await r.json();
  return j.emails || {};
}

export async function adminGetUsers(query) {
  const callerUserId = await ensureAuth();
  const r = await fetch("/api/admin/users", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ callerUserId, ...query }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `Failed to load users (HTTP ${r.status})`);
  }
  return r.json();
}

export async function adminGetActivity(query) {
  const callerUserId = await ensureAuth();
  const r = await fetch("/api/admin/activity", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ callerUserId, ...query }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `Failed to load activity (HTTP ${r.status})`);
  }
  return r.json();
}

export async function adminGetSystemHealth(opts = {}) {
  const callerUserId = await ensureAuth();
  const r = await fetch("/api/admin/system-health", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ callerUserId, ...opts }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `Failed to load system health (HTTP ${r.status})`);
  }
  return r.json();
}

export async function adminGetCronDetail(kind) {
  const callerUserId = await ensureAuth();
  const r = await fetch(`/api/admin/system-health/cron/${encodeURIComponent(kind)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ callerUserId }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `Failed to load cron detail (HTTP ${r.status})`);
  }
  return r.json();
}

export async function adminGetReports(opts = {}) {
  const callerUserId = await ensureAuth();
  const r = await fetch("/api/admin/reports", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ callerUserId, ...opts }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `Failed to load reports (HTTP ${r.status})`);
  }
  return r.json();
}

export async function adminGetCostUsage(opts = {}) {
  const callerUserId = await ensureAuth();
  const r = await fetch("/api/admin/cost-usage", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ callerUserId, ...opts }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `Failed to load cost & usage (HTTP ${r.status})`);
  }
  return r.json();
}

export async function adminGetCostUsageProvider(provider, opts = {}) {
  const callerUserId = await ensureAuth();
  const r = await fetch(`/api/admin/cost-usage/provider/${encodeURIComponent(provider)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ callerUserId, ...opts }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `Failed to load provider drill-down (HTTP ${r.status})`);
  }
  return r.json();
}

export async function adminGetCostUsageWorkspace(workspaceId, opts = {}) {
  const callerUserId = await ensureAuth();
  const r = await fetch(`/api/admin/cost-usage/workspace/${encodeURIComponent(workspaceId)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ callerUserId, ...opts }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `Failed to load workspace drill-down (HTTP ${r.status})`);
  }
  return r.json();
}

export async function adminGetAtRisk(opts = {}) {
  const callerUserId = await ensureAuth();
  const r = await fetch("/api/admin/at-risk", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ callerUserId, ...opts }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `Failed to load at-risk workspaces (HTTP ${r.status})`);
  }
  return r.json();
}

export async function adminGetAtRiskWorkspace(workspaceId) {
  const callerUserId = await ensureAuth();
  const r = await fetch(`/api/admin/at-risk/workspace/${encodeURIComponent(workspaceId)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ callerUserId }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `Failed to load workspace drill-down (HTTP ${r.status})`);
  }
  return r.json();
}

export async function adminRecomputeAtRisk(opts = {}) {
  const callerUserId = await ensureAuth();
  const r = await fetch("/api/admin/at-risk/recompute", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ callerUserId, ...opts }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `Failed to recompute (HTTP ${r.status})`);
  }
  return r.json();
}

export async function adminGetWhiteLabel(opts = {}) {
  const callerUserId = await ensureAuth();
  const r = await fetch("/api/admin/white-label", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ callerUserId, ...opts }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `Failed to load white-label configs (HTTP ${r.status})`);
  }
  return r.json();
}

export async function adminRecheckWhiteLabel(id) {
  const callerUserId = await ensureAuth();
  const r = await fetch(`/api/admin/white-label/${encodeURIComponent(id)}/recheck`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ callerUserId }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `Failed to recheck (HTTP ${r.status})`);
  }
  return r.json();
}

export async function adminRecheckAllWhiteLabel(opts = {}) {
  const callerUserId = await ensureAuth();
  const r = await fetch("/api/admin/white-label/recheck-all", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ callerUserId, ...opts }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `Failed to bulk recheck (HTTP ${r.status})`);
  }
  return r.json();
}

export async function adminWhiteLabelAction(id, action) {
  const callerUserId = await ensureAuth();
  const r = await fetch(`/api/admin/white-label/${encodeURIComponent(id)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ callerUserId, action }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `Failed to perform action (HTTP ${r.status})`);
  }
  return r.json();
}

export async function adminAddUserToWorkspace(userId, workspaceId, role) {
  const callerUserId = await ensureAuth();
  const r = await fetch("/api/admin/add-user-to-workspace", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ callerUserId, userId, workspaceId, role }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `Failed to add user (HTTP ${r.status})`);
  }
  return r.json(); // { ok, action: "added" | "updated" }
}

export async function adminUpdateWorkspaceMemberRole(userId, workspaceId, role) {
  const callerUserId = await ensureAuth();
  const r = await fetch("/api/admin/update-workspace-member-role", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ callerUserId, userId, workspaceId, role }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update role (HTTP ${r.status})`);
  }
  return r.json();
}

export async function adminRemoveWorkspaceMember(userId, workspaceId) {
  const callerUserId = await ensureAuth();
  const r = await fetch("/api/admin/remove-workspace-member", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ callerUserId, userId, workspaceId }),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `Failed to remove member (HTTP ${r.status})`);
  }
  return r.json();
}

// Check whether any workspace already records the given website URL.
// Hits the server endpoint (service-role) which scans business-profile +
// business-brain in app_data across ALL workspaces. Returns
// { exists: bool, host: string|null, workspaceName?: string }.
// Falls back to { exists: false } if the request errors out — we'd rather
// allow creation than block legitimate signups on a flaky network.
export async function checkWebsiteDomain(website, excludeWorkspaceId) {
  if (!website) return { exists: false };
  try {
    const r = await fetch("/api/workspaces/check-website-domain", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ website, excludeWorkspaceId: excludeWorkspaceId || null }),
    });
    if (!r.ok) return { exists: false };
    return await r.json();
  } catch { return { exists: false }; }
}

export async function createWorkspace(name, category = "restaurant") {
  if (!supabase) throw new Error("Supabase not configured");
  const uid = await ensureAuth();
  const { data, error } = await supabase.from("workspaces").insert({ name, category, owner_id: uid }).select().single();
  if (error) throw new Error(error.message);
  await supabase.from("workspace_members").insert({ workspace_id: data.id, user_id: uid, role: "owner" });
  return data;
}

export async function switchWorkspace(workspaceId) {
  if (!supabase) return;
  const uid = await ensureAuth();
  await supabase.from("profiles").update({ active_workspace_id: workspaceId }).eq("id", uid);
  _workspaceId = workspaceId;
  _accountId   = null; // will re-derive if needed
}

// ── Profile ──────────────────────────────────────────────────────────────────

export async function getProfile(userId) {
  if (!supabase) return null;
  const uid = userId || await ensureAuth();
  const { data } = await supabase.from("profiles").select("*").eq("id", uid).single();
  if (data) {
    _accountId = data.account_id;
    if (data.active_workspace_id) _workspaceId = data.active_workspace_id;
  }
  return data;
}

export async function upsertProfile(fields) {
  if (!supabase) return;
  const uid = await ensureAuth();
  await supabase.from("profiles").upsert({ id: uid, ...fields }, { onConflict: "id" });
}

// ── Team management ───────────────────────────────────────────────────────────

export async function getTeamMembers(workspaceId) {
  if (!supabase) return [];
  // When viewing another workspace, use server endpoint (supabaseAdmin bypasses RLS)
  if (workspaceId) {
    const r = await fetch(`/api/admin/workspace-team?workspaceId=${encodeURIComponent(workspaceId)}`);
    const d = await r.json();
    return d.team || [];
  }
  const accountId = await getAccountId();
  const { data } = await supabase
    .from("profiles")
    .select("id, email, display_name, role, created_at")
    .eq("account_id", accountId)
    .order("created_at");
  return data || [];
}

export async function getPendingInvites(workspaceId) {
  if (!supabase) return [];
  // When viewing another workspace, use server endpoint (supabaseAdmin bypasses RLS)
  if (workspaceId) {
    const r = await fetch(`/api/admin/workspace-team?workspaceId=${encodeURIComponent(workspaceId)}`);
    const d = await r.json();
    return d.invites || [];
  }
  const accountId = await getAccountId();
  const { data } = await supabase
    .from("team_invites")
    .select("*")
    .eq("account_id", accountId)
    .is("accepted_at", null)
    .order("created_at", { ascending: false });
  return data || [];
}

export async function inviteTeamMember(email, role) {
  if (!supabase) throw new Error("Supabase not configured");
  const accountId = await getAccountId();
  const wid       = await getWorkspaceId().catch(() => null);

  // Server-side: inserts invite record + sends invite email via Supabase admin API.
  // Admin invite tokens last 24h and are not consumed by email security scanners
  // (unlike signInWithOtp magic links which are single-use OTPs).
  const r = await fetch("/api/team/send-invite", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, role, accountId, workspaceId: wid }),
  });
  const d = await r.json();
  if (!r.ok) throw new Error(d.error || "Failed to send invite");
  return d; // { ok: true, existing?: true, inviteLink?: string }
}

export async function revokeInvite(inviteId) {
  const r = await fetch("/api/team/revoke-invite", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ inviteId }),
  });
  const d = await r.json();
  if (!r.ok) throw new Error(d.error || "Revoke failed");
}

// Server-side invite acceptance — uses service role to bypass RLS so new users
// can read their own invite record before a profile/account_id exists.
export async function acceptPendingInvite(userEmail) {
  if (!supabase) return null;
  const uid = await ensureAuth();
  const r = await fetch("/api/team/accept-invite", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId: uid, userEmail }),
  });
  const d = await r.json();
  if (!r.ok) return null;
  if (!d.invite) return null;
  // Sync local cache so subsequent getAccountId/getWorkspaceId calls return correct values
  _accountId   = d.invite.account_id;
  if (d.invite.workspace_id) _workspaceId = d.invite.workspace_id;
  return d.invite;
}

export async function removeMember(memberId) {
  const accountId = await getAccountId();
  const r = await fetch("/api/team/remove-member", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ memberId, accountId }),
  });
  const d = await r.json();
  if (!r.ok) throw new Error(d.error || "Remove failed");
}

export async function updateMemberRole(memberId, role) {
  const r = await fetch("/api/team/update-role", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ memberId, role }),
  });
  const d = await r.json();
  if (!r.ok) throw new Error(d.error || "Role update failed");
}

// ── Auth ─────────────────────────────────────────────────────────────────────

export async function signUp(email, password) {
  return supabase.auth.signUp({ email, password, options: { emailRedirectTo: window.location.origin } });
}

export async function signIn(email, password) {
  return supabase.auth.signInWithPassword({ email, password });
}

export async function signInWithEmail(email) {
  const res = await fetch("/api/auth/magic-link", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, redirectTo: window.location.origin }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to send sign-in link");
  return { data, error: null };
}

export async function signInWithGoogle() {
  return supabase.auth.signInWithOAuth({
    provider: "google",
    options: { redirectTo: window.location.origin },
  });
}

export async function resetPassword(email) {
  const res = await fetch("/api/auth/reset-password", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, redirectTo: window.location.origin }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to send reset link");
  return { data, error: null };
}

export async function updatePassword(password) {
  return supabase.auth.updateUser({ password });
}

export async function signOut() {
  _userId = null; _accountId = null;
  return supabase.auth.signOut();
}
